import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, Trash2, AlertTriangle, Mail, Send, Download, Upload, Loader2, Key, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { devError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const [settings, setSettings] = useState({
    deletion_password: '',
    test_mode: false,
    notification_email: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [clearTestDialogOpen, setClearTestDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPassword, setImportPassword] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [searchParams] = useSearchParams();

  // Change own password
  const [myOldPass, setMyOldPass] = useState('');
  const [myNewPass, setMyNewPass] = useState('');
  const [myNewPass2, setMyNewPass2] = useState('');
  const [changingPass, setChangingPass] = useState(false);
  const [mustChangePass, setMustChangePass] = useState(false);

  // Staff users
  const [staffUsers, setStaffUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [newStaff, setNewStaff] = useState({ email: '', name: '', role: 'gestor', password: '', location_ids: [] });
  const [creatingStaff, setCreatingStaff] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchMe();
    fetchStaffUsers();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API}/locations`, { credentials: 'include' });
      if (res.ok) setLocations(await res.json());
    } catch (e) { devError('fetchLocations', e); }
  };

  const fetchMe = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setMustChangePass(!!d.must_change_password);
        // Se vindo do login forçado, scroll para a secção
        if (searchParams.get('force') === 'change-password') {
          setTimeout(() => {
            const el = document.querySelector('[data-testid="change-password-section"]');
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
        }
      }
    } catch (e) { devError('fetchMe', e); }
  };

  const fetchStaffUsers = async () => {
    try {
      const res = await fetch(`${API}/auth/users`, { credentials: 'include' });
      if (res.ok) setStaffUsers(await res.json());
    } catch (e) { devError('fetchStaffUsers', e); }
  };

  const handleChangeMyPassword = async () => {
    if (myNewPass.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    if (myNewPass !== myNewPass2) {
      toast.error('A confirmação não coincide com a nova senha');
      return;
    }
    setChangingPass(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_password: myOldPass, new_password: myNewPass }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao alterar senha');
      toast.success('Senha alterada com sucesso');
      setMyOldPass(''); setMyNewPass(''); setMyNewPass2('');
      setMustChangePass(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChangingPass(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!newStaff.email || !newStaff.name || !newStaff.password) {
      toast.error('Preencha email, nome e senha');
      return;
    }
    if (newStaff.password.length < 8) {
      toast.error('Senha deve ter pelo menos 8 caracteres');
      return;
    }
    setCreatingStaff(true);
    try {
      const res = await fetch(`${API}/auth/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStaff),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao criar');
      toast.success(`${data.name} criado. Deverá trocar a senha no primeiro login.`);
      setNewStaff({ email: '', name: '', role: 'gestor', password: '', location_ids: [] });
      fetchStaffUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingStaff(false);
    }
  };

  const handleResetStaffPassword = async (email) => {
    const newP = window.prompt(`Nova senha para ${email} (min. 8 caracteres):`);
    if (!newP) return;
    if (newP.length < 8) { toast.error('Senha muito curta'); return; }
    try {
      const res = await fetch(`${API}/auth/reset-user-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, new_password: newP }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      toast.success(data.message);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteStaff = async (user_id, email) => {
    if (!window.confirm(`Remover utilizador ${email}?`)) return;
    try {
      const res = await fetch(`${API}/auth/users/${user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      toast.success(data.message);
      fetchStaffUsers();
    } catch (err) { toast.error(err.message); }
  };

  const handleUpdateStaffLocations = async (u, newLocationIds) => {
    try {
      const res = await fetch(`${API}/auth/users/${u.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ location_ids: newLocationIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao atualizar locais');
      }
      toast.success('Locais atualizados');
      fetchStaffUsers();
    } catch (err) { toast.error(err.message); }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setNotificationEmail(data.notification_email || '');
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationEmail = async () => {
    const trimmed = notificationEmail.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Email inválido');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notification_email: trimmed || null })
      });
      if (!res.ok) throw new Error('save failed');
      toast.success(trimmed ? 'Email de notificação salvo' : 'Notificações por email desativadas');
      fetchSettings();
    } catch (error) {
      toast.error('Erro ao salvar email');
    } finally {
      setSavingEmail(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await fetch(`${API}/settings/test-email`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Erro ao enviar email de teste');
        return;
      }
      toast.success(`Email de teste enviado para ${data.to}`);
    } catch (error) {
      toast.error('Erro ao enviar email de teste (rede)');
    } finally {
      setSendingTest(false);
    }
  };

  const updatePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 4) {
      toast.error('A senha deve ter pelo menos 4 caracteres');
      return;
    }

    try {
      const res = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ deletion_password: newPassword })
      });

      if (!res.ok) throw new Error('Erro ao atualizar');
      
      toast.success('Senha de exclusão atualizada');
      setNewPassword('');
      setConfirmPassword('');
      fetchSettings();
    } catch (error) {
      toast.error('Erro ao atualizar senha');
    }
  };

  const toggleTestMode = async () => {
    try {
      const res = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ test_mode: !settings.test_mode })
      });

      if (!res.ok) throw new Error('Erro ao atualizar');
      
      toast.success(`Modo teste ${!settings.test_mode ? 'ativado' : 'desativado'}`);
      fetchSettings();
    } catch (error) {
      toast.error('Erro ao alterar modo teste');
    }
  };

  const clearTestData = async () => {
    try {
      const res = await fetch(`${API}/test-mode/clear`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Erro ao limpar');
      
      toast.success('Dados de teste apagados');
      setClearTestDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao limpar dados de teste');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/admin/export-all`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `timetrack_backup_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descarregado');
    } catch (err) {
      toast.error('Erro ao exportar backup');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecione um ficheiro .json');
      return;
    }
    if (!importPassword) {
      toast.error('Digite a senha de exclusão');
      return;
    }
    setImporting(true);
    setImportSummary(null);
    try {
      const text = await importFile.text();
      const backup = JSON.parse(text);
      if (!backup || !backup.collections) {
        throw new Error('Ficheiro de backup inválido');
      }
      const res = await fetch(`${API}/admin/import-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: importPassword, wipe_existing: true, backup }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro ao importar');
      setImportSummary(data.summary || {});
      toast.success('Backup importado com sucesso');
      setImportPassword('');
      setImportFile(null);
    } catch (err) {
      toast.error(err.message || 'Erro ao importar backup');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Configurações">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configurações">
      <div className="max-w-2xl space-y-8">
        {/* Change my password */}
        <section
          className={`border-2 p-6 ${mustChangePass ? 'border-[#DC2626] bg-[#FEF2F2]' : 'border-[#111111]'}`}
          data-testid="change-password-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <Key className={`w-6 h-6 ${mustChangePass ? 'text-[#DC2626]' : 'text-[#111111]'}`} />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Alterar Minha Senha
            </h2>
          </div>
          {mustChangePass && (
            <div className="bg-[#FEE2E2] border-2 border-[#DC2626] p-3 mb-4 text-xs text-[#991B1B]">
              ⚠️ <strong>Troca obrigatória:</strong> defina uma nova senha para continuar a usar o sistema.
            </div>
          )}
          <div className="space-y-3">
            {!mustChangePass && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest">Senha Atual</Label>
                <Input
                  type="password"
                  value={myOldPass}
                  onChange={(e) => setMyOldPass(e.target.value)}
                  autoComplete="current-password"
                  data-testid="my-old-password"
                  className="h-11"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Nova Senha (mín. 8 caracteres)</Label>
              <Input
                type="password"
                value={myNewPass}
                onChange={(e) => setMyNewPass(e.target.value)}
                autoComplete="new-password"
                data-testid="my-new-password"
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Confirmar Nova Senha</Label>
              <Input
                type="password"
                value={myNewPass2}
                onChange={(e) => setMyNewPass2(e.target.value)}
                autoComplete="new-password"
                data-testid="my-new-password-confirm"
                className="h-11"
              />
            </div>
            <Button
              onClick={handleChangeMyPassword}
              disabled={changingPass}
              data-testid="change-my-password-btn"
              className="bg-[#111111] text-white hover:bg-[#0029FF]"
            >
              {changingPass ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Alterar Senha
            </Button>
          </div>
        </section>

        {/* Staff user management (Admin only) */}
        <section className="border border-[#E5E7EB] p-6" data-testid="staff-users-section">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Utilizadores do Sistema (Admin/RH/Gestor)
            </h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-4">
            Crie contas para outros utilizadores administrativos. Cada um recebe uma senha temporária que deve trocar no primeiro login.
          </p>

          {/* Create new staff */}
          <div className="border-2 border-dashed border-[#E5E7EB] p-4 mb-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Email</Label>
                <Input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  placeholder="nome@pontodg.com"
                  className="h-10"
                  data-testid="new-staff-email"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Nome</Label>
                <Input
                  value={newStaff.name}
                  onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                  placeholder="Nome completo"
                  className="h-10"
                  data-testid="new-staff-name"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Função</Label>
                <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
                  <SelectTrigger className="h-10" data-testid="new-staff-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="rh">RH / Financeiro</SelectItem>
                    <SelectItem value="gestor">Gestor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Senha Temporária</Label>
                <Input
                  type="password"
                  value={newStaff.password}
                  onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                  placeholder="Min. 8 caracteres"
                  className="h-10"
                  data-testid="new-staff-password"
                />
              </div>
            </div>

            {/* Location multi-select — só para role=gestor */}
            {newStaff.role === 'gestor' && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest">Locais a que este gestor tem acesso</Label>
                <div className="flex flex-wrap gap-2 p-2 border border-[#E5E7EB] rounded min-h-[44px] mt-1" data-testid="new-staff-locations">
                  {locations.length === 0 && (
                    <span className="text-xs text-[#9CA3AF] italic">Nenhum local criado ainda.</span>
                  )}
                  {locations.map((loc) => {
                    const active = newStaff.location_ids.includes(loc.location_id);
                    return (
                      <button
                        type="button"
                        key={loc.location_id}
                        onClick={() => {
                          const next = active
                            ? newStaff.location_ids.filter((id) => id !== loc.location_id)
                            : [...newStaff.location_ids, loc.location_id];
                          setNewStaff({ ...newStaff, location_ids: next });
                        }}
                        className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest border-2 transition-all ${
                          active
                            ? 'bg-[#F59E0B] text-white border-[#F59E0B]'
                            : 'bg-white text-[#111111] border-[#E5E7EB] hover:border-[#F59E0B]'
                        }`}
                        data-testid={`new-staff-loc-${loc.location_id}`}
                      >
                        {loc.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-1">Deixe vazio para dar acesso a todas as casas.</p>
              </div>
            )}
            <Button
              onClick={handleCreateStaff}
              disabled={creatingStaff}
              data-testid="create-staff-btn"
              className="bg-[#059669] hover:bg-[#047857] text-white"
            >
              {creatingStaff ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Criar Utilizador
            </Button>
          </div>

          {/* Staff list */}
          <div className="space-y-2">
            {staffUsers.length === 0 && (
              <p className="text-sm text-[#9CA3AF] italic">Nenhum outro utilizador cadastrado.</p>
            )}
            {staffUsers.map((u) => {
              const userLocs = u.location_ids || [];
              const isGestor = u.role === 'gestor';
              return (
              <div key={u.user_id} className="flex flex-col gap-2 p-3 border border-[#E5E7EB]" data-testid={`staff-row-${u.user_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{u.name}</div>
                    <div className="text-xs text-[#6B7280]">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#F3F4F6]">
                      {u.role === 'admin' ? 'ADMIN' : u.role === 'rh' ? 'RH' : 'GESTOR'}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResetStaffPassword(u.email)}
                      data-testid={`reset-pw-${u.user_id}`}
                    >
                      Nova senha
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteStaff(u.user_id, u.email)}
                      data-testid={`delete-staff-${u.user_id}`}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
                {isGestor && (
                  <div className="flex flex-wrap gap-1.5 pt-1 border-t border-[#F3F4F6]">
                    <span className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mr-2 mt-1">Casas:</span>
                    {locations.map((loc) => {
                      const active = userLocs.includes(loc.location_id);
                      return (
                        <button
                          type="button"
                          key={loc.location_id}
                          onClick={() => {
                            const next = active
                              ? userLocs.filter((id) => id !== loc.location_id)
                              : [...userLocs, loc.location_id];
                            handleUpdateStaffLocations(u, next);
                          }}
                          className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest border ${
                            active
                              ? 'bg-[#F59E0B] text-white border-[#F59E0B]'
                              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#F59E0B]'
                          }`}
                          data-testid={`staff-loc-${u.user_id}-${loc.location_id}`}
                        >
                          {loc.name}
                        </button>
                      );
                    })}
                    {userLocs.length === 0 && (
                      <span className="text-[10px] text-[#DC2626] italic mt-1">Sem casa atribuída (não verá dados)</span>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </section>

        {/* Deletion Password */}
        <section className="border border-[#E5E7EB] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Senha de Exclusão
            </h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">
            Esta senha é necessária para excluir permanentemente funcionários arquivados.
          </p>
          
          <div className="space-y-4">
            <div>
              <Label>Nova Senha</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha"
              />
            </div>
            <div>
              <Label>Confirmar Senha</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme a senha"
              />
            </div>
            <Button onClick={updatePassword} data-testid="update-password-btn">
              Atualizar Senha
            </Button>
          </div>
        </section>

        {/* Backup / Restore */}
        <section className="border-2 border-[#0029FF] p-6" data-testid="backup-restore-section">
          <div className="flex items-center gap-3 mb-4">
            <Download className="w-6 h-6 text-[#0029FF]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Backup e Migração de Dados
            </h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-4">
            Exporte todos os dados reais (funcionários, escalas, locais, registos, ajustes e pedidos) para um ficheiro JSON.
            Útil para <strong>migrar do preview para o seu domínio publicado</strong> — exporte aqui, entre em produção e importe.
          </p>
          <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] p-3 mb-4 text-xs text-[#92400E]">
            ⚠️ <strong>A importação SUBSTITUI os dados existentes</strong> (exceto registos marcados como teste). Guarde sempre um backup antes de importar.
            Fotos do Quiosque não são incluídas no backup (expiram em 90 dias por RGPD).
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-backup-btn"
              className="bg-[#0029FF] hover:bg-[#0022CC] text-white"
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A exportar...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Exportar Backup (.json)</>
              )}
            </Button>
          </div>

          <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
            <Label className="text-xs font-bold uppercase tracking-widest text-[#111111]">Importar Backup</Label>
            <div className="space-y-3">
              <Input
                type="file"
                accept="application/json,.json"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportSummary(null); }}
                data-testid="import-file-input"
                className="h-11"
              />
              <Input
                type="password"
                placeholder="Senha de exclusão (para confirmar)"
                value={importPassword}
                onChange={(e) => setImportPassword(e.target.value)}
                data-testid="import-password-input"
                className="h-11"
              />
              <Button
                onClick={handleImport}
                disabled={importing || !importFile || !importPassword}
                data-testid="import-backup-btn"
                variant="destructive"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A importar...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Importar e substituir</>
                )}
              </Button>
            </div>
            {importSummary && (
              <div className="mt-3 p-3 bg-[#F0FDF4] border border-[#059669] text-xs text-[#065F46]" data-testid="import-summary">
                <div className="font-bold mb-1">✓ Importação concluída:</div>
                <ul className="space-y-0.5">
                  {Object.entries(importSummary).map(([col, stats]) => (
                    <li key={col}>
                      <code className="font-mono">{col}</code>: {stats.imported} registos ({stats.inserted} novos, {stats.updated} atualizados)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Test Mode */}
        <section className="border border-[#E5E7EB] p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Modo Teste
            </h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">
            O modo teste permite criar dados de demonstração que podem ser apagados sem afetar os registros reais.
          </p>
          
          <div className="flex items-center justify-between p-4 bg-[#F3F4F6] mb-4">
            <div>
              <p className="font-medium">Modo Teste</p>
              <p className="text-sm text-[#6B7280]">
                {settings.test_mode ? 'Ativo - dados criados serão marcados como teste' : 'Inativo'}
              </p>
            </div>
            <Switch
              data-testid="test-mode-switch"
              checked={settings.test_mode}
              onCheckedChange={toggleTestMode}
            />
          </div>

          <Button 
            variant="destructive"
            onClick={() => setClearTestDialogOpen(true)}
            data-testid="clear-test-data-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar Dados de Teste
          </Button>
        </section>

        {/* Email Notifications */}
        <section className="border border-[#E5E7EB] p-6" data-testid="email-notifications-section">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Notificações por Email
            </h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">
            Receba um email automático sempre que um funcionário criar uma nova solicitação
            (Férias, Falta ou Troca de Turno) no portal. Deixe em branco para desativar.
          </p>

          <div className="space-y-4">
            <div>
              <Label>Email do destinatário</Label>
              <Input
                data-testid="notification-email-input"
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                placeholder="avisos@minhaempresa.com"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={saveNotificationEmail}
                disabled={savingEmail}
                data-testid="save-notification-email-btn"
              >
                {savingEmail ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="outline"
                onClick={sendTestEmail}
                disabled={sendingTest || !settings.notification_email}
                data-testid="send-test-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingTest ? 'Enviando...' : 'Enviar email de teste'}
              </Button>
            </div>
            {!settings.notification_email && (
              <p className="text-xs text-[#9CA3AF]">
                Notificações por email atualmente desativadas.
              </p>
            )}
          </div>
        </section>

        {/* System Info */}
        <section className="border border-[#E5E7EB] p-6">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Satoshi, sans-serif' }}>
            Informações do Sistema
          </h2>
          <div className="space-y-2 text-sm">
            <p><span className="text-[#6B7280]">Versão:</span> 1.0.0</p>
            <p><span className="text-[#6B7280]">Última atualização:</span> {settings.updated_at ? new Date(settings.updated_at).toLocaleString('pt-BR') : '-'}</p>
          </div>
        </section>
      </div>

      {/* Clear Test Data Dialog */}
      <AlertDialog open={clearTestDialogOpen} onOpenChange={setClearTestDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar Dados de Teste</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá apagar todos os dados marcados como teste, incluindo:
              <ul className="list-disc ml-6 mt-2">
                <li>Funcionários de teste</li>
                <li>Registros de ponto de teste</li>
                <li>Escalas de teste</li>
                <li>Solicitações de teste</li>
                <li>Alertas de teste</li>
              </ul>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={clearTestData}
              className="bg-[#DC2626] hover:bg-[#DC2626]/90"
              data-testid="confirm-clear-test-btn"
            >
              Limpar Dados de Teste
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
